import type { Trait } from '@equiview/protocol';
import type { Medida } from '../dados/fonte.js';

const ESCALA = [-2, -1, 0, 1, 2] as const;
const ROTULO_LADO = { E: 'Esquerdo', D: 'Direito', NA: '' } as const;

/**
 * Aprumo: escala de -2 a +2 (0 = correto), um toque por lado. O sentido de cada
 * extremo vem da definicao do protocolo ("-: acampado · +: remetido"), para o
 * avaliador nao precisar lembrar qual lado da escala e qual desvio.
 */
export function CampoAprumo({
  trait,
  valores,
  desabilitado,
  onSalvar,
}: {
  trait: Trait;
  valores: ReadonlyArray<Pick<Medida, 'side' | 'value_num' | 'not_applicable'>>;
  desabilitado?: boolean;
  onSalvar: (side: Medida['side'], valor: number | null, naoSeAplica: boolean) => void;
}) {
  const lados = trait.sided ? (['E', 'D'] as const) : (['NA'] as const);
  const [neg, pos] = sentidos(trait.definition);

  return (
    <fieldset className="space-y-2 border-b border-borda pb-4 last:border-b-0 last:pb-0">
      <legend className="text-sm font-medium">
        {trait.label} <span className="font-mono text-xs text-apagado">{trait.code}</span>
      </legend>
      {neg && (
        <p className="text-xs text-apagado">
          −2 {neg} · 0 correto · +2 {pos}
        </p>
      )}
      <div className="space-y-2">
        {lados.map((lado) => {
          const atual = valores.find((v) => v.side === lado);
          const na = atual?.not_applicable === true;
          return (
            <div key={lado} className="flex flex-wrap items-center gap-2">
              {lado !== 'NA' && <span className="w-16 text-xs text-apagado">{ROTULO_LADO[lado]}</span>}
              <div role="radiogroup" aria-label={`${trait.label}${lado !== 'NA' ? `, lado ${ROTULO_LADO[lado]}` : ''}`} className="inline-flex rounded-(--radius-controle) border border-borda-forte">
                {ESCALA.map((n, i) => {
                  const ativo = !na && atual?.value_num === n;
                  return (
                    <button
                      key={n}
                      type="button"
                      role="radio"
                      aria-checked={ativo}
                      disabled={desabilitado}
                      onClick={() => onSalvar(lado, ativo ? null : n, false)}
                      className={`numero min-h-11 min-w-11 text-base font-medium transition-colors disabled:opacity-60 ${
                        i > 0 ? 'border-l border-borda-forte' : ''
                      } ${ativo ? 'bg-marca text-sobre-marca' : 'bg-superficie hover:bg-destaque-fundo'}`}
                    >
                      {n > 0 ? `+${n}` : n === 0 ? '0' : `−${-n}`}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                aria-pressed={na}
                disabled={desabilitado}
                onClick={() => onSalvar(lado, null, !na)}
                className={`min-h-11 rounded-(--radius-controle) border px-3 text-[13px] font-medium disabled:opacity-60 ${
                  na ? 'border-marca bg-marca text-sobre-marca' : 'border-borda-forte text-apagado hover:text-texto'
                }`}
              >
                Não se aplica
              </button>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}

/** "Desvio negativo: estreita. Desvio positivo: larga. ..." -> ['estreita', 'larga']. */
function sentidos(definicao: string): [string | null, string | null] {
  const neg = definicao.match(/Desvio negativo: ([^.]+)\./)?.[1] ?? null;
  const pos = definicao.match(/Desvio positivo: ([^.]+)\./)?.[1] ?? null;
  return [neg, pos];
}
